// Updated to use the Shopify Storefront API client and new API methods
import { createStorefrontApiClient } from "@shopify/storefront-api-client";

export default class {
	constructor(options) {
		this.defaults = {
			$el: null,
		};
		this.options = {};
		Object.assign(this.options, this.defaults, options);

		this.namespace = "chipsShop";

		// Keep your original site name and API key, but use the new API client
		this.client = createStorefrontApiClient({
			storeDomain: "judd-foundation-store.myshopify.com",
			apiVersion: "2024-04",
			publicAccessToken: "c93fc4ad777e3c7953b65b6e2730b779",
		});

		// Define storage key and GraphQL queries/mutations for the new API
		this.CART_ID_STORAGE_KEY = "shopifyCartId";

		this.cartCreateMutation = `
      mutation ($input: CartInput!, $country: CountryCode, $language: LanguageCode)
      @inContext(country: $country, language: $language) {
        cartCreate(input: $input) {
          userErrors {
            message
            code
            field
          }
          cart {
            id
            checkoutUrl
          }
        }
      }`;

		this.cartQuery = `
      query cartQuery($cartId: ID!) {
        cart(id: $cartId) {
          id
          checkoutUrl
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    priceV2 {
                      amount
                      currencyCode
                    }
                    product {
                      title
                    }
                  }
                }
              }
            }
          }
          estimatedCost {
            subtotalAmount {
              amount
              currencyCode
            }
          }
        }
      }`;

		this.cartLinesAddMutation = `
      mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            id
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      priceV2 {
                        amount
                        currencyCode
                      }
                      product {
                        title
                      }
                    }
                  }
                }
              }
            }
            estimatedCost {
              subtotalAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }`;

		this.cartLinesUpdateMutation = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            id
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      priceV2 {
                        amount
                        currencyCode
                      }
                      product {
                        title
                      }
                    }
                  }
                }
              }
            }
            estimatedCost {
              subtotalAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }`;

		this.cartLinesRemoveMutation = `
      mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            id
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      priceV2 {
                        amount
                        currencyCode
                      }
                      product {
                        title
                      }
                    }
                  }
                }
              }
            }
            estimatedCost {
              subtotalAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }`;

		// Initialize component
		this.init(true);
	}

	// Get or create a cart ID using the new API
	async getCartId() {
		let cartId = localStorage.getItem(this.CART_ID_STORAGE_KEY);
		if (!cartId) {
			const { data, errors } = await this.client.request(
				this.cartCreateMutation,
				{
					variables: { input: {}, country: "US", language: "EN" },
				}
			);
			if (errors) {
				console.error("Error creating cart:", errors);
				return null;
			}
			cartId = data.cartCreate.cart.id;
			localStorage.setItem(this.CART_ID_STORAGE_KEY, cartId);
		}
		return cartId;
	}

	// Initialize event listeners and product UI
	init(bool) {
		if (bool) {
			const appScope = this;
			// Update the cart if items exist (cart will be managed via the API)
			this.updateCart();

			// Bind cart UI events
			if (document.querySelector(".your-cart")) {
				document
					.querySelector(".your-cart")
					.addEventListener("click", function () {
						appScope.openCart();
					});
			}
			if (document.querySelector(".modal-bg")) {
				document
					.querySelector(".modal-bg")
					.addEventListener("click", function () {
						document
							.querySelector(".checkout-modal")
							.classList.remove("modal-on");
					});
			}

			// Process buy buttons and build product UI using the new API
			const buyButtons = document.querySelectorAll(".buy-button");
			if (buyButtons && buyButtons.length > 0) {
				buyButtons.forEach((el) => {
					const scopeThis = el;
					const buyText = el.dataset.text;
					const unavailableText = el.dataset.unavailable;
					const handle = scopeThis.dataset.id;
					if (handle) {
						const productId = "gid://shopify/Product/" + handle;
						const productQuery = `
              query ($id: ID!) {
                node(id: $id) {
                  ... on Product {
                    id
                    title
                    availableForSale
                    variants(first: 250) {
                      edges {
                        node {
                          id
                          title
                          availableForSale
                          priceV2 {
                            amount
                            currencyCode
                          }
                        }
                      }
                    }
                  }
                }
              }`;
						this.client
							.request(productQuery, {
								variables: { id: productId },
							})
							.then(({ data }) => {
								const product = data?.node;
								if (
									product &&
									product.availableForSale &&
									product.variants &&
									product.variants.edges
								) {
									const productTitle = product.title;
									const variants = product.variants.edges
										.filter((edge) => edge && edge.node)
										.map((edge) => edge.node);
									if (variants.length > 1) {
										// Build UI for multiple variants
										let price = variants[0].priceV2.amount;
										if (price.endsWith(".00"))
											price = price.replace(".00", "");
										else if (price.endsWith(".0"))
											price = price.replace(".0", "");
										scopeThis.innerHTML =
											'<span class="blok product-price">$' +
											price +
											"</span>" +
											'<div class="select-wrap"><select id="formvariant"></select></div>' +
											'<div class="button-wrap"><button class="addCartVariant">' +
											buyText +
											'<span class="product-title">' +
											productTitle +
											"</span></button></div>";
										const selectEl =
											scopeThis.querySelector("select");
										variants.forEach((variant) => {
											let outPrice =
												variant.priceV2.amount;
											if (outPrice.endsWith(".00"))
												outPrice = outPrice.replace(
													".00",
													""
												);
											else if (outPrice.endsWith(".0"))
												outPrice = outPrice.replace(
													".0",
													""
												);
											if (variant.availableForSale) {
												selectEl.innerHTML +=
													'<option value="' +
													variant.id +
													'" data-price="' +
													outPrice +
													'">' +
													variant.title +
													"</option>";
											} else {
												selectEl.innerHTML +=
													'<option value="' +
													variant.id +
													'" data-price="' +
													outPrice +
													'" disabled>' +
													variant.title +
													" (Sold out)</option>";
											}
										});
										scopeThis
											.querySelector(".addCartVariant")
											.addEventListener("click", () => {
												const selectedOption =
													selectEl.options[
														selectEl.selectedIndex
													];
												const variantId =
													selectEl.value;
												const prodTitleCombined =
													productTitle +
													" " +
													selectedOption.textContent;
												const prodPrice =
													selectedOption.getAttribute(
														"data-price"
													);
												appScope.chipsCartAdd(
													variantId,
													prodTitleCombined,
													prodPrice
												);
											});
									} else {
										// Build UI for a single variant
										let price = variants[0].priceV2.amount;
										if (price.endsWith(".00"))
											price = price.replace(".00", "");
										else if (price.endsWith(".0"))
											price = price.replace(".0", "");
										scopeThis.innerHTML =
											'<span class="blok product-price">$' +
											price +
											"</span>" +
											'<button class="addCart" data-variant="' +
											variants[0].id +
											'" data-price="' +
											price +
											'">' +
											buyText +
											'<span class="product-title">' +
											productTitle +
											"</span></button>";
										scopeThis
											.querySelector(".addCart")
											.addEventListener(
												"click",
												function () {
													const variantId =
														this.getAttribute(
															"data-variant"
														);
													const prodTitleSingle =
														productTitle;
													const prodPriceSingle =
														this.getAttribute(
															"data-price"
														);
													appScope.chipsCartAdd(
														variantId,
														prodTitleSingle,
														prodPriceSingle
													);
												}
											);
									}
								} else {
									scopeThis.innerHTML =
										'<button class="addCart unavailable">' +
										unavailableText +
										"</button>";
								}
							});
					}
				});
			}
		}
	}

	// Update the cart UI by fetching the latest cart using the new API
	async updateCart() {
		const cartId = await this.getCartId();
		if (!cartId) return;

		const { data, errors } = await this.client.request(this.cartQuery, {
			variables: { cartId },
		});
		if (errors) {
			console.error("Error fetching cart:", errors);
			return;
		}
		const cart = data.cart;
		if (!cart) {
			console.warn(
				"Cart not found — possibly expired or invalid. Resetting."
			);
			localStorage.removeItem(this.CART_ID_STORAGE_KEY);
			await this.getCartId(); // triggers a new cart creation
			return this.updateCart(); // recall updateCart with new cart
		}
		let totalCartCount = 0;
		let cartItemsHTML = "";
		if (cart.lines && cart.lines.edges && cart.lines.edges.length > 0) {
			cart.lines.edges.forEach((edge) => {
				if (!edge || !edge.node) return;
				const node = edge.node;
				totalCartCount += node.quantity;
				const variant = node.merchandise;
				const prodTitle = variant.product.title + " - " + variant.title;
				let price = variant.priceV2.amount;
				if (price.endsWith(".00")) price = price.replace(".00", "");
				else if (price.endsWith(".0")) price = price.replace(".0", "");
				const qtyControls = `
          <div class="qty-wrap">
            <button data-line-id="${node.id}" class="qty-dec"></button>
            <span class="qty-single">${node.quantity}</span>
            <button data-line-id="${node.id}" class="qty-inc"></button>
          </div>`;
				let lineTotal = (parseFloat(price) * node.quantity).toFixed(2);
				lineTotal = lineTotal.replace(".00", "");
				cartItemsHTML += `
          <div class="product-single">
            <span class="product-title">${prodTitle}</span>
            ${qtyControls}
            <span class="cart-remove" data-line-id="${node.id}">Remove</span>
            <span class="product-price">${lineTotal}</span>
          </div>`;
			});
			document.querySelector(".checkout-modal .cart-items").innerHTML =
				cartItemsHTML;
			document.querySelectorAll(".cart-remove").forEach((btn) => {
				btn.addEventListener("click", () => {
					const lineId = btn.getAttribute("data-line-id");
					this.chipsCartRemove(lineId);
				});
			});
		} else {
			document
				.querySelector(".checkout-modal")
				.classList.remove("modal-on");
		}
		const yourCartEl = document.querySelector(".your-cart");
		const cartCountEl = yourCartEl.querySelector("small");
		if (totalCartCount > 0) {
			yourCartEl.classList.add("has-qty");
			if (cartCountEl)
				cartCountEl.innerHTML = `:&#8202;${totalCartCount}`;
		} else {
			yourCartEl.classList.remove("has-qty");
			if (cartCountEl) cartCountEl.innerHTML = "";
		}
		this.bindQtyButtons();
	}

	// Add an item to the cart via the new API
	async chipsCartAdd(merchandiseId, prodTitle, prodPrice) {
		const cartId = await this.getCartId();
		if (!cartId) return;
		const { data, errors } = await this.client.request(
			this.cartLinesAddMutation,
			{
				variables: { cartId, lines: [{ merchandiseId, quantity: 1 }] },
			}
		);
		if (errors) {
			console.error("Error adding line item:", errors);
			return;
		}
		this.updateCart();
		this.openCart();
	}

	// Remove an item from the cart via the new API
	async chipsCartRemove(lineId) {
		const cartId = await this.getCartId();
		if (!cartId) return;
		const { data, errors } = await this.client.request(
			this.cartLinesRemoveMutation,
			{
				variables: { cartId, lineIds: [lineId] },
			}
		);
		if (errors) {
			console.error("Error removing line item:", errors);
			return;
		}
		this.updateCart();
	}

	// Update the quantity for a cart line using the new API
	async chipsCartQty(lineId, newQuantity) {
		const cartId = await this.getCartId();
		if (!cartId) return;
		const { data, errors } = await this.client.request(
			this.cartLinesUpdateMutation,
			{
				variables: {
					cartId,
					lines: [{ id: lineId, quantity: newQuantity }],
				},
			}
		);
		if (errors) {
			console.error("Error updating line item:", errors);
			return;
		}
		this.updateCart();
		this.openCart();
	}

	// Bind quantity increase/decrease button events
	bindQtyButtons() {
		const qtyIncButtons = document.querySelectorAll(".qty-inc");
		qtyIncButtons.forEach((btn) => {
			if (!btn.classList.contains("init")) {
				btn.classList.add("init");
				btn.addEventListener("click", () => {
					const lineId = btn.getAttribute("data-line-id");
					const qtyEl =
						btn.parentElement.querySelector(".qty-single");
					let currentQty = parseInt(qtyEl.textContent);
					this.chipsCartQty(lineId, currentQty + 1);
				});
			}
		});
		const qtyDecButtons = document.querySelectorAll(".qty-dec");
		qtyDecButtons.forEach((btn) => {
			if (!btn.classList.contains("init")) {
				btn.classList.add("init");
				btn.addEventListener("click", () => {
					const lineId = btn.getAttribute("data-line-id");
					const qtyEl =
						btn.parentElement.querySelector(".qty-single");
					let currentQty = parseInt(qtyEl.textContent);
					if (currentQty > 1) {
						this.chipsCartQty(lineId, currentQty - 1);
					} else {
						this.chipsCartRemove(lineId);
					}
				});
			}
		});
	}

	// Open the cart modal and update the checkout link using the new API
	async openCart() {
		const cartId = await this.getCartId();
		if (!cartId) return;
		document.querySelector(".checkout-modal").classList.add("modal-on");
		const { data, errors } = await this.client.request(this.cartQuery, {
			variables: { cartId },
		});
		if (errors) {
			console.error("Error fetching cart:", errors);
			return;
		}
		const checkoutUrl = data.cart.checkoutUrl;
		if (checkoutUrl) {
			const checkoutLinkEl = document.querySelector(".checkout-link");
			if (checkoutLinkEl) {
				checkoutLinkEl.setAttribute("href", checkoutUrl);
			}
		}
		this.updateCart();
	}
}
